<template>
  <b-form @submit.prevent="submit">
    <b-row>
      <b-col cols="12" class="h5 font-weight-bold mb-4"> Edit Method Components </b-col>

      <!-- Method Overview -->
      <b-col cols="12" class="mb-4">
        <label for="input__method-overview" class="mb-u4"> Method overview </label>
        <base-text-editor
          id="input__method-overview"
          v-model="$v.form.method_overview.$model"
          name="input__method-overview"
          :class="{
            text_editor__required: $v.form.method_overview.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your method overview"
        ></base-text-editor>
        <div
          v-if="$v.form.method_overview.$anyError"
          id="input__method-overview-invalid-feedback"
          class="text_feedback"
        >
          The Method overview is a required field.
        </div>
      </b-col>

      <!-- Method -->
      <b-col cols="12" class="mb-4">
        <label for="input__method" class="mb-u4"> Method </label>
        <base-text-editor
          id="input__method"
          v-model="$v.form.method.$model"
          name="input__method"
          class="ql-editor--large"
          :class="{
            text_editor__required: $v.form.method.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your method"
        ></base-text-editor>
        <div
          v-if="$v.form.method.$anyError"
          id="input__method--invalid-feedback"
          class="text_feedback"
        >
          The Method is a required field.
        </div>
      </b-col>

      <!-- Reference -->
      <b-col cols="12" class="mb-4">
        <label for="input__reference" class="mb-u4"> Reference </label>
        <base-text-editor
          id="input__reference"
          v-model="$v.form.reference.$model"
          name="input__reference"
          :class="{
            text_editor__required: $v.form.reference.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your reference"
        ></base-text-editor>
        <div
          v-if="$v.form.reference.$anyError"
          id="input__reference--invalid-feedback"
          class="text_feedback"
        >
          The Reference is a required field.
        </div>
      </b-col>

      <!-- Learning Resource -->
      <b-col cols="12" class="mb-4">
        <label for="input__learning-resource" class="mb-u4"> Learning Resource </label>
        <base-text-editor
          id="input__learning-resource"
          v-model="$v.form.learning_resource.$model"
          name="input__learning-resource"
          :class="{
            text_editor__required: $v.form.learning_resource.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your learning resource"
        ></base-text-editor>
        <div
          v-if="$v.form.learning_resource.$anyError"
          id="input__learning-resource--invalid-feedback"
          class="text_feedback"
        >
          The Learning Resource is a required field.
        </div>
      </b-col>

      <!-- How can you achieve -->
      <b-col cols="12" class="mb-4">
        <label for="input__how-can-achieve" class="mb-u4"> How can you achieve </label>
        <base-text-editor
          id="input__how-can-achieve"
          v-model="$v.form.how_achieve.$model"
          name="input__how-can-achieve"
          :class="{
            text_editor__required: $v.form.how_achieve.$anyError
          }"
          :disabled="disabled"
          placeholder="Insert your how can you achieve"
        ></base-text-editor>
        <div
          v-if="$v.form.how_achieve.$anyError"
          id="input__how-can-achieve--invalid-feedback"
          class="text_feedback"
        >
          The How can you achieve is a required field.
        </div>
      </b-col>

      <b-col cols="12">
        <hr class="mb-4" />
      </b-col>

      <!-- Button -->
      <b-col cols="12" class="mb-4">
        <b-button variant="success" class="btn__create-mc mr-2" :disabled="disabled" type="submit">
          <div v-if="disabled" class="mb-u1">
            <b-spinner label="Loading..." variant="secondary" small />
          </div>
          <div v-else>
            {{ learningUnit && learningUnit.id ? 'Save' : 'Create' }}
          </div>
        </b-button>
        <b-button
          variant="link"
          class="btn__back"
          :disabled="disabled"
          @click="$_pageNavigationMixin_gotoPreviousPage"
        >
          Back to previous page
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
// import { required } from "vuelidate/lib/validators";
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import LearningUnit from '@/models/LearningUnit';
import { notificationMixin } from '@/mixins/notificationMixin';
import { pageNavigationMixin } from '@/mixins/pageNavigationMixin';

export default {
  components: {
    BaseTextEditor
  },
  mixins: [notificationMixin, pageNavigationMixin],

  props: {
    learningUnit: {
      type: LearningUnit,
      default: null
    }
  },

  data() {
    return {
      disabled: false,
      onSubmit: false,
      form: {
        method_overview: '',
        method: '',
        reference: '',
        learning_resource: '',
        how_achieve: ''
      }
    };
  },

  validations: {
    form: {
      method_overview: {},
      method: {},
      reference: {},
      learning_resource: {},
      how_achieve: {}
    }
  },

  mounted() {
    this.initMicroCredential();
  },

  methods: {
    initMicroCredential() {
      if (!this.learningUnit) {
        this.notiError();
        return;
      }

      const { learningUnit, form } = this;

      for (const [key] of Object.entries(form)) {
        this.form[key] = learningUnit[key];
      }
    },

    async update() {
      try {
        this.disabled = true;

        await LearningUnit.api().updateLearningUnit(this.learningUnit.id, {
          ...this.form
        });

        this.notiSuccessDefault(`The Micro-Credentials was successfully updated`);
        this.onSubmit = false;
        this.$_pageNavigationMixin_gotoPreviousPage();
      } catch (error) {
        console.log(error);
        this.notiError();
      } finally {
        this.disabled = false;
      }
    },

    submit() {
      this.$v.form.$touch();
      this.onSubmit = true;

      if (this.$v.form.$invalid) {
        return;
      }

      if (this.learningUnit && this.learningUnit.id) {
        this.update();
      }
    }
  }
};
</script>

<style lang="scss">
.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}

.text_feedback {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #f83245;
}
</style>
